import React from 'react'
import { motion, MotionProps, useTransform, useScroll } from 'framer-motion'
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver'
import { usePrefersReducedMotion } from '../../hooks/useResponsive'

interface ScrollRevealProps extends Omit<MotionProps, 'initial' | 'animate' | 'variants'> {
  children: React.ReactNode
  direction?: 'up' | 'down' | 'left' | 'right' | 'scale' | 'fade'
  delay?: number
  duration?: number
  distance?: number
  triggerOnce?: boolean
  threshold?: number
  className?: string
}

export function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.6,
  distance = 50,
  triggerOnce = true,
  threshold = 0.1,
  className,
  ...motionProps
}: ScrollRevealProps) {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    triggerOnce,
    threshold,
  })
  const prefersReducedMotion = usePrefersReducedMotion()

  const getVariants = () => {
    switch (direction) {
      case 'up':
        return {
          hidden: { opacity: 0, y: prefersReducedMotion ? 0 : distance },
          visible: { opacity: 1, y: 0 },
        }
      case 'down':
        return {
          hidden: { opacity: 0, y: prefersReducedMotion ? 0 : -distance },
          visible: { opacity: 1, y: 0 },
        }
      case 'left':
        return {
          hidden: { opacity: 0, x: prefersReducedMotion ? 0 : distance },
          visible: { opacity: 1, x: 0 },
        }
      case 'right':
        return {
          hidden: { opacity: 0, x: prefersReducedMotion ? 0 : -distance },
          visible: { opacity: 1, x: 0 },
        }
      case 'scale':
        return {
          hidden: { opacity: 0, scale: prefersReducedMotion ? 1 : 0.8 },
          visible: { opacity: 1, scale: 1 },
        }
      case 'fade':
        return {
          hidden: { opacity: 0 },
          visible: { opacity: 1 },
        }
      default:
        return {
          hidden: { opacity: 0 },
          visible: { opacity: 1 },
        }
    }
  }

  const baseTransition = {
    duration: prefersReducedMotion ? 0.01 : duration,
    delay: prefersReducedMotion ? 0 : delay,
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isIntersecting ? 'visible' : 'hidden'}
      variants={getVariants()}
      transition={baseTransition}
      className={className}
      {...motionProps}
    >
      {children}
    </motion.div>
  )
}

// Staggered scroll reveal for lists
interface StaggeredScrollRevealProps {
  children: React.ReactNode[]
  direction?: ScrollRevealProps['direction']
  staggerDelay?: number
  itemDelay?: number
  duration?: number
  distance?: number
  threshold?: number
  className?: string
  itemClassName?: string
}

export function StaggeredScrollReveal({
  children,
  direction = 'up',
  staggerDelay = 0.1,
  itemDelay = 0,
  duration = 0.6,
  distance = 50,
  threshold = 0.1,
  className,
  itemClassName,
}: StaggeredScrollRevealProps) {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    triggerOnce: true,
    threshold,
  })
  const prefersReducedMotion = usePrefersReducedMotion()

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : staggerDelay,
        delayChildren: prefersReducedMotion ? 0 : itemDelay,
      },
    },
  }

  const getItemVariants = () => {
    switch (direction) {
      case 'up':
        return {
          hidden: { opacity: 0, y: prefersReducedMotion ? 0 : distance },
          visible: { opacity: 1, y: 0 },
        }
      case 'down':
        return {
          hidden: { opacity: 0, y: prefersReducedMotion ? 0 : -distance },
          visible: { opacity: 1, y: 0 },
        }
      case 'left':
        return {
          hidden: { opacity: 0, x: prefersReducedMotion ? 0 : distance },
          visible: { opacity: 1, x: 0 },
        }
      case 'right':
        return {
          hidden: { opacity: 0, x: prefersReducedMotion ? 0 : -distance },
          visible: { opacity: 1, x: 0 },
        }
      case 'scale':
        return {
          hidden: { opacity: 0, scale: prefersReducedMotion ? 1 : 0.8 },
          visible: { opacity: 1, scale: 1 },
        }
      default:
        return {
          hidden: { opacity: 0 },
          visible: { opacity: 1 },
        }
    }
  }

  const itemTransition = {
    duration: prefersReducedMotion ? 0.01 : duration,
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isIntersecting ? 'visible' : 'hidden'}
      variants={containerVariants}
      className={className}
    >
      {children.map((child, index) => (
        <motion.div
          key={index}
          variants={getItemVariants()}
          transition={itemTransition}
          className={itemClassName}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  )
}

// Parallax scroll effect
interface ParallaxScrollProps {
  children: React.ReactNode
  speed?: number
  className?: string
}

export function ParallaxScroll({
  children,
  speed = 0.5,
  className,
}: ParallaxScrollProps) {
  const prefersReducedMotion = usePrefersReducedMotion()

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>
  }

  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 1000], [0, -1000 * speed])

  return (
    <motion.div
      className={className}
      style={{ y }}
    >
      {children}
    </motion.div>
  )
}